import { useState } from 'react';
import { ApiError, saveBlob } from '@nyce/admin-kit/api';
import { KeyValues, StatusChip, type Tone } from '@nyce/admin-kit/console';
import { Button } from '@nyce/admin-kit/ui';
import type { DbServerRef } from '../api/db';
import { formatBytes, formatCount } from '../lib/format';
import { serverName } from '../pages/databases/shared';
import type {
  AdviceStatement,
  CandidateReview,
  Coverage,
  ExistingIndex,
  ReviewStatus,
  ScanStatus,
  TableStats,
  Verdict,
} from './api/indexAdvisor';

export { useParam } from '../pages/statistics/shared';

/** Under a read replica's server: the primary whose database it is (counted once, as the primary's). */
export function ReplicaOf({ servers, primary }: { servers: DbServerRef[]; primary?: string | null }) {
  if (!primary) return null;
  return <span className="iax-muted iax-replica">replica of {serverName(servers, primary)}</span>;
}

/** The page's toast (`useNotice`), for the parts that act. */
export interface Notify {
  success: (text: string) => void;
  error: (text: string) => void;
}

/** Server errors in operator words. */
export function advisorErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return 'You lack the permission for this action (needs ophub:db:advise).';
    if (e.code === 'scan_running') return 'A scan is already queued or running.';
    if (e.code === 'not_running') return 'The scan is no longer queued or running.';
    if (e.code === 'llm_not_configured') return 'The review is not configured on this hub.';
    if (e.code === 'not_reviewable')
      return 'Only a succeeded scan without a review in progress can be reviewed.';
    if (e.code === 'unknown_database') return e.message || 'That database does not exist on the server.';
    if (e.code === 'db_unreachable') return e.message || 'The database server cannot be reached.';
  }
  return (e as Error)?.message || String(e);
}

const COVERAGE: Record<Coverage, { tone: Tone; label: string; help: string }> = {
  new: { tone: 'blue', label: 'new', help: 'Not in the default set.' },
  in_default: { tone: 'green', label: 'in default set', help: 'Exactly this index is in the default set.' },
  covered: { tone: 'grey', label: 'covered', help: 'A default-set index already starts with these columns.' },
  extends: { tone: 'amber', label: 'extends', help: 'Adds columns to a default-set index.' },
  unparsed: { tone: 'grey', label: 'unparsed', help: 'The advisor’s text could not be parsed.' },
};

export const COVERAGE_OPTIONS = (Object.keys(COVERAGE) as Coverage[]).map((c) => ({
  value: c,
  label: COVERAGE[c].label,
}));

/** new / in default set / covered by X / extends X / unparsed. */
export function CoverageChip({ coverage, coveredBy }: { coverage: Coverage; coveredBy?: string | null }) {
  const c = COVERAGE[coverage] ?? COVERAGE.unparsed;
  const by = (coverage === 'covered' || coverage === 'extends') && coveredBy ? ` ${coveredBy}` : '';
  return (
    <span title={c.help} data-coverage={coverage} className="iax-chip">
      <StatusChip
        value={coverage}
        tone={c.tone}
        label={`${c.label}${coverage === 'covered' && by ? ' by' : ''}${by}`}
      />
    </span>
  );
}

const VERDICT: Record<Verdict, { tone: Tone; label: string }> = {
  add: { tone: 'green', label: 'add' },
  widen: { tone: 'amber', label: 'widen' },
  reorder: { tone: 'amber', label: 'reorder' },
  covered: { tone: 'grey', label: 'covered' },
  conditional: { tone: 'blue', label: 'conditional' },
  skip: { tone: 'grey', label: 'skip' },
};

export const VERDICT_OPTIONS = (Object.keys(VERDICT) as Verdict[]).map((v) => ({
  value: v,
  label: VERDICT[v].label,
}));

export function VerdictChip({ review }: { review: Pick<CandidateReview, 'verdict' | 'confidence'> }) {
  const v = VERDICT[review.verdict] ?? { tone: 'grey' as Tone, label: review.verdict };
  return (
    <span className="iax-verdict" data-verdict={review.verdict}>
      <StatusChip value={review.verdict} tone={v.tone} label={v.label} />
      <span className="iax-muted"> {review.confidence} confidence</span>
    </span>
  );
}

const RUN_TONE: Record<ScanStatus | ReviewStatus, Tone> = {
  none: 'grey',
  queued: 'blue',
  running: 'blue',
  succeeded: 'green',
  failed: 'red',
  cancelled: 'grey',
};

export function RunChip({ status, label }: { status: ScanStatus | ReviewStatus; label?: string }) {
  return (
    <span data-status={status} className="iax-chip">
      <StatusChip value={status} tone={RUN_TONE[status] ?? 'grey'} label={label ?? status} />
    </span>
  );
}

export const formatMs = (ms: number | null | undefined) =>
  ms == null
    ? '—'
    : ms < 1000
      ? `${Math.round(ms)} ms`
      : ms < 60_000
        ? `${(ms / 1000).toFixed(1)} s`
        : `${(ms / 60_000).toFixed(1)} min`;

export const formatShare = (share: number) => `${Math.round(share * 100)} %`;

/** The statements behind a recommendation, with calls and total time where the advisor has them. */
export function Statements({
  statements,
  databases,
}: {
  statements: (AdviceStatement & { databases?: number })[];
  /** Show the "databases" count per statement (fleet evidence). */
  databases?: boolean;
}) {
  if (!statements.length) return <p className="nyk-ui-hint">No statements recorded.</p>;
  return (
    <ul className="iax-statements">
      {statements.map((s, i) => (
        <li key={i}>
          <pre className="iax-sql">{s.query}</pre>
          <span className="nyk-ui-hint">
            {databases && s.databases != null && <>{formatCount(s.databases)} databases · </>}
            {s.calls != null ? `${formatCount(s.calls)} calls` : 'calls unknown'}
            {s.totalMs != null && ` · ${formatMs(s.totalMs)} total`}
            {s.calls && s.totalMs != null ? ` · ${formatMs(s.totalMs / s.calls)} mean` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** One table's size, write rate and scans. */
export function TableStatsView({ stats }: { stats: TableStats }) {
  return (
    <KeyValues
      rows={[
        ['Rows', formatCount(stats.rows)],
        ['Size', formatBytes(stats.totalBytes)],
        [
          'Writes',
          `${formatCount(stats.inserts)} ins · ${formatCount(stats.updates)} upd · ${formatCount(stats.deletes)} del`,
        ],
        ['Scans', `${formatCount(stats.seqScans)} seq · ${formatCount(stats.idxScans)} index`],
      ]}
    />
  );
}

/** A table's existing indexes with how often they are used. */
export function ExistingIndexes({ indexes, label }: { indexes: ExistingIndex[]; label: string }) {
  if (!indexes.length) return <p className="nyk-ui-hint">No indexes on this table.</p>;
  return (
    <table className="nyk-ui-table nyk-cards iax-indexes" aria-label={label}>
      <thead>
        <tr>
          <th>Index</th>
          <th>Definition</th>
          <th className="nyk-ui-num">Scans</th>
        </tr>
      </thead>
      <tbody>
        {indexes.map((x) => (
          <tr key={x.name}>
            <td className="nyk-card-title nyk-wrap">{x.name}</td>
            <td data-label="Definition" className="nyk-mono nyk-wrap">
              {x.def}
            </td>
            <td data-label="Scans" className="nyk-ui-num">
              <span className={x.scans === 0 ? 'iax-unused' : undefined}>{formatCount(x.scans)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Saves `text` as a `.sql` file. */
export const saveSql = (text: string, name: string) =>
  saveBlob(new Blob([text], { type: 'application/sql;charset=utf-8' }), name);

/** A toggle for the detail under a list row (`aria-expanded`). */
export function ExpandButton({
  open,
  onToggle,
  children,
  controls,
}: {
  open: boolean;
  onToggle: () => void;
  children: string;
  controls?: string;
}) {
  return (
    <Button size="sm" variant="ghost" aria-expanded={open} aria-controls={controls} onClick={onToggle}>
      {open ? `Hide ${children.toLowerCase()}` : children}
    </Button>
  );
}

/** Ids of the rows whose detail is open. */
export function useOpenRows() {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (id: string) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  return { isOpen: (id: string) => open.has(id), toggle };
}
